// src/admin/pages/HealthPage.js
// Health page showing error list

import React, { useState, useEffect } from 'react';
import { 
  AlertTriangle,
  RefreshCw,
  Clock,
  Trash2,
  Eye,
  X,
  CheckCircle
} from 'lucide-react';
import { simpleErrorTracking } from '../../services/simpleErrorTracking';
import { supabase } from '../../config';

const HealthPage = () => {
  const [errors, setErrors] = useState([]);
  const [dbErrors, setDbErrors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [selectedError, setSelectedError] = useState(null);
  const [errorSource, setErrorSource] = useState('all'); // 'all', 'memory', 'database'

  const loadErrors = async () => {
    setLoading(true);
    try {
      // Get errors from in-memory tracking
      const errorSummary = simpleErrorTracking.getErrorSummary();
      const memoryErrors = errorSummary.recentErrors || [];

      // Get errors from database (audit_log table)
      let databaseErrors = [];
      try {
        const { data, error } = await supabase
          .from('audit_log')
          .select('*')
          .eq('table_name', 'error_log')
          .order('timestamp', { ascending: false })
          .limit(100);

        if (!error && data) {
          databaseErrors = data.map(log => ({
            id: log.record_id,
            message: log.new_values?.message || 'Unknown error',
            stack: log.new_values?.stack,
            context: log.new_values?.context,
            url: log.new_values?.url,
            user_agent: log.user_agent,
            timestamp: log.timestamp,
            user_id: log.user_id,
            source: 'database'
          }));
        }
      } catch (err) {
        console.warn('Error fetching database errors:', err);
      }

      setErrors(memoryErrors);
      setDbErrors(databaseErrors);
      setLastUpdated(() => {
        const date = new Date();
        const day = date.getDate();
        const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        const month = monthNames[date.getMonth()];
        const year = date.getFullYear();
        const hours = date.getHours().toString().padStart(2, '0');
        const minutes = date.getMinutes().toString().padStart(2, '0');
        return `${day} ${month} ${year}, ${hours}:${minutes}`;
      });
    } catch (error) {
      console.error('Error loading errors:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadErrors();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    let interval;
    if (autoRefresh) {
      interval = setInterval(loadErrors, 30000); // Refresh every 30 seconds
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [autoRefresh]); // eslint-disable-line react-hooks/exhaustive-deps

  const clearAllErrors = () => {
    if (window.confirm('Clear all in-memory errors? (Database errors will remain)')) {
      simpleErrorTracking.clearOldErrors(0); // Clear all
      loadErrors();
    }
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'Unknown';
    try {
      const date = new Date(timestamp);
      if (isNaN(date.getTime())) return 'Unknown';
      const day = date.getDate();
      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const month = monthNames[date.getMonth()];
      const year = date.getFullYear();
      const hours = date.getHours().toString().padStart(2, '0');
      const minutes = date.getMinutes().toString().padStart(2, '0');
      return `${day} ${month} ${year}, ${hours}:${minutes}`;
    } catch {
      return timestamp;
    }
  };

  const getErrorType = (error) => {
    return error.context?.type || error.context?.errorType || 'unknown';
  };

  const getErrorSeverity = (error) => {
    const type = getErrorType(error);
    if (type === 'global_error' || type === 'unhandled_promise_rejection') {
      return 'high';
    }
    if (type === 'api_error') {
      return 'medium';
    }
    return 'low';
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'high':
        return 'text-red-600 bg-red-100 border-red-200';
      case 'medium':
        return 'text-yellow-600 bg-yellow-100 border-yellow-200';
      case 'low':
        return 'text-blue-600 bg-blue-100 border-blue-200';
      default:
        return 'text-gray-600 bg-gray-100 border-gray-200';
    }
  };

  const allErrors = errorSource === 'all' 
    ? [...errors.map(e => ({ ...e, source: 'memory' })), ...dbErrors]
    : errorSource === 'memory'
    ? errors.map(e => ({ ...e, source: 'memory' }))
    : dbErrors;

  // Sort by timestamp (newest first)
  const sortedErrors = allErrors.sort((a, b) => {
    const timeA = new Date(a.timestamp || 0).getTime();
    const timeB = new Date(b.timestamp || 0).getTime();
    return timeB - timeA;
  });

  if (loading && errors.length === 0 && dbErrors.length === 0) {
    return (
      <div className="flex justify-center items-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
        <p className="ml-4 text-lg text-gray-700">Loading errors...</p>
      </div>
    );
  }

  return (
    <div className="w-full max-w-full bg-gray-50 min-h-screen">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">System Health</h1>
          <p className="text-gray-600 mt-1">Error logs and system issues</p>
        </div>
        <div className="flex items-center space-x-4">
          {lastUpdated && (
            <span className="text-sm text-gray-500 flex items-center">
              <Clock className="h-4 w-4 mr-1" />
              Updated: {lastUpdated}
            </span>
          )}
          <label className="flex items-center text-gray-700">
            <input
              type="checkbox"
              className="form-checkbox h-5 w-5 text-blue-600"
              checked={autoRefresh}
              onChange={() => setAutoRefresh(!autoRefresh)}
            />
            <span className="ml-2">Auto Refresh</span>
          </label>
          <button
            onClick={loadErrors}
            className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 flex items-center"
          >
            <RefreshCw className="h-4 w-4 mr-2" /> Refresh
          </button>
          {errors.length > 0 && (
            <button
              onClick={clearAllErrors}
              className="px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600 flex items-center"
            >
              <Trash2 className="h-4 w-4 mr-2" /> Clear Memory Errors
            </button>
          )}
        </div>
      </div>

      {/* Error Source Filter */}
      <div className="mb-6 bg-white rounded-lg shadow p-4">
        <div className="flex items-center space-x-4">
          <span className="text-sm font-medium text-gray-700">Error Source:</span>
          <button
            onClick={() => setErrorSource('all')}
            className={`px-3 py-1 rounded-md text-sm ${
              errorSource === 'all'
                ? 'bg-blue-500 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            All ({errors.length + dbErrors.length})
          </button>
          <button
            onClick={() => setErrorSource('memory')}
            className={`px-3 py-1 rounded-md text-sm ${
              errorSource === 'memory'
                ? 'bg-blue-500 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Memory ({errors.length})
          </button>
          <button
            onClick={() => setErrorSource('database')}
            className={`px-3 py-1 rounded-md text-sm ${
              errorSource === 'database'
                ? 'bg-blue-500 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Database ({dbErrors.length})
          </button>
        </div>
      </div>

      {/* Error Summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
        <div className="bg-white shadow rounded-lg p-6">
          <div className="flex items-center">
            <AlertTriangle className="h-6 w-6 text-red-600 mr-3" />
            <div>
              <p className="text-sm font-medium text-gray-600">Total Errors</p>
              <p className="text-2xl font-bold text-gray-900">{sortedErrors.length}</p>
            </div>
          </div>
        </div>
        <div className="bg-white shadow rounded-lg p-6">
          <div className="flex items-center">
            <AlertTriangle className="h-6 w-6 text-yellow-600 mr-3" />
            <div>
              <p className="text-sm font-medium text-gray-600">Memory Errors</p>
              <p className="text-2xl font-bold text-gray-900">{errors.length}</p>
            </div>
          </div>
        </div>
        <div className="bg-white shadow rounded-lg p-6">
          <div className="flex items-center">
            <AlertTriangle className="h-6 w-6 text-blue-600 mr-3" />
            <div>
              <p className="text-sm font-medium text-gray-600">Database Errors</p>
              <p className="text-2xl font-bold text-gray-900">{dbErrors.length}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Error List */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
          <h3 className="text-lg font-semibold text-gray-900">Recent Errors</h3>
        </div>

        {sortedErrors.length === 0 ? (
          <div className="p-8 text-center">
            <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900">No Errors</h3>
            <p className="text-sm text-gray-500 mt-2">
              No errors have been recorded. The system is running smoothly.
            </p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {sortedErrors.map((error, index) => {
              const severity = getErrorSeverity(error);
              return (
                <div
                  key={error.id || index}
                  className="p-4 hover:bg-gray-50 cursor-pointer"
                  onClick={() => setSelectedError(selectedError?.id === error.id ? null : error)}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-2">
                        <span className={`px-2 py-1 rounded text-xs font-medium border ${getSeverityColor(severity)}`}>
                          {severity.toUpperCase()}
                        </span>
                        <span className="px-2 py-1 rounded text-xs font-medium bg-gray-100 text-gray-700">
                          {getErrorType(error)}
                        </span>
                        {error.source && (
                          <span className="px-2 py-1 rounded text-xs font-medium bg-blue-100 text-blue-700">
                            {error.source}
                          </span>
                        )}
                      </div>
                      <p className="font-medium text-gray-900">{error.message || 'Unknown error'}</p>
                      {error.url && (
                        <p className="text-sm text-gray-500 mt-1">{error.url}</p>
                      )}
                      <p className="text-xs text-gray-400 mt-1">
                        {formatTimestamp(error.timestamp)}
                      </p>
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedError(selectedError?.id === error.id ? null : error);
                      }}
                      className="ml-4 text-gray-400 hover:text-gray-600"
                    >
                      {selectedError?.id === error.id ? (
                        <X className="h-5 w-5" />
                      ) : (
                        <Eye className="h-5 w-5" />
                      )}
                    </button>
                  </div>

                  {selectedError?.id === error.id && (
                    <div className="mt-4 pt-4 border-t border-gray-200">
                      <div className="space-y-2">
                        {error.stack && (
                          <div>
                            <p className="text-xs font-medium text-gray-700 mb-1">Stack Trace:</p>
                            <pre className="text-xs bg-gray-50 p-3 rounded overflow-auto max-h-40">
                              {error.stack}
                            </pre>
                          </div>
                        )}
                        {error.context && Object.keys(error.context).length > 0 && (
                          <div>
                            <p className="text-xs font-medium text-gray-700 mb-1">Context:</p>
                            <pre className="text-xs bg-gray-50 p-3 rounded overflow-auto max-h-40">
                              {JSON.stringify(error.context, null, 2)}
                            </pre>
                          </div>
                        )}
                        {error.user_agent && (
                          <div>
                            <p className="text-xs font-medium text-gray-700 mb-1">User Agent:</p>
                            <p className="text-xs text-gray-600">{error.user_agent}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default HealthPage;
